import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { api } from '../lib/api.ts';
import { Icon } from '../lib/icons.tsx';
import { live } from '../lib/live.ts';
import { Markdown } from '../lib/markdown.tsx';
import { loadCatalog, saveEnabledModels, useModelCatalog } from '../lib/model-catalog.ts';
import {
  AGENT_TOOL_IDS,
  type AgentId,
  type AgentToolId,
  type CliUpdateRow,
  type CliUpdateSnapshot,
  type CommandSnapshot,
  type HostPref,
  type ProviderCatalog,
  type ProviderModel,
} from '../lib/protocol.ts';
import { Composer } from './Composer.tsx';
import { ChoiceGroup } from './ChoiceGroup.tsx';
import { displayHost, parsePort, readStoredPort, storePort, type ProjectRow } from '../lib/project.ts';

const VISION_REWRITE_MODEL = 'composer-2.5' as const;

const VISION_REWRITE_PROMPT = `Read vision.md at the project root and rewrite it so it is clean, well-structured markdown.

Keep the meaning and product direction. Improve headings, spacing, lists, and wording so it reads well. Do not invent new goals or drop existing ones.

Write the rewritten text back to vision.md. Do not change any other files.`;

export type ConfigSubTab =
  | 'backlog'
  | 'vision'
  | 'files'
  | 'projects'
  | 'models'
  | 'updates'
  | 'commands';

const TOOL_OPTIONS: { id: AgentToolId; label: string }[] = [
  { id: 'agent-task', label: 'Task' },
  { id: 'agent-video', label: 'Video' },
  { id: 'agent-shot', label: 'Shot' },
];

function toolsOf(project: { tools?: AgentToolId[] }): AgentToolId[] {
  return project.tools ?? [...AGENT_TOOL_IDS];
}

function withTool(current: AgentToolId[], id: AgentToolId, on: boolean): AgentToolId[] {
  const next = new Set(current);
  if (on) next.add(id);
  else next.delete(id);
  return AGENT_TOOL_IDS.filter((tool) => next.has(tool));
}

export function ConfigSubTabs({
  tab,
  onTab,
  showCommands = false,
}: {
  tab: ConfigSubTab;
  onTab: (tab: ConfigSubTab) => void;
  showCommands?: boolean;
}): JSX.Element {
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!showCommands && tab === 'commands') onTab('vision');
  }, [showCommands, tab, onTab]);

  // Seven tabs are wider than a phone, so the strip scrolls; the one you are
  // looking at has to be the one on screen.
  useEffect(() => {
    navRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [tab]);

  return (
    <nav
      ref={navRef}
      className="git-subtabs git-subtabs-dense"
      role="tablist"
      aria-label="More views"
    >
      <button
        type="button"
        role="tab"
        id="tab-backlog"
        aria-selected={tab === 'backlog'}
        aria-controls="pane-backlog"
        className={`git-subtab${tab === 'backlog' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('backlog')}
      >
        Backlog
      </button>
      <button
        type="button"
        role="tab"
        id="tab-vision"
        aria-selected={tab === 'vision'}
        aria-controls="pane-vision"
        className={`git-subtab${tab === 'vision' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('vision')}
      >
        Vision
      </button>
      <button
        type="button"
        role="tab"
        id="tab-files"
        aria-selected={tab === 'files'}
        aria-controls="pane-files"
        className={`git-subtab${tab === 'files' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('files')}
      >
        Files
      </button>
      <button
        type="button"
        role="tab"
        id="tab-config-projects"
        aria-selected={tab === 'projects'}
        aria-controls="pane-config-projects"
        className={`git-subtab${tab === 'projects' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('projects')}
      >
        Projects
      </button>
      <button
        type="button"
        role="tab"
        id="tab-config-models"
        aria-selected={tab === 'models'}
        aria-controls="pane-config-models"
        className={`git-subtab${tab === 'models' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('models')}
      >
        Models
      </button>
      <button
        type="button"
        role="tab"
        id="tab-config-updates"
        aria-selected={tab === 'updates'}
        aria-controls="pane-config-updates"
        className={`git-subtab${tab === 'updates' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('updates')}
      >
        Updates
      </button>
      {showCommands && (
        <button
          type="button"
          role="tab"
          id="tab-config-commands"
          aria-selected={tab === 'commands'}
          aria-controls="pane-config-commands"
          className={`git-subtab${tab === 'commands' ? ' git-subtab-on' : ''}`}
          onClick={() => onTab('commands')}
        >
          Commands
        </button>
      )}
    </nav>
  );
}

export function VisionView({
  projectId,
  host,
  hidden,
  onOpen,
}: {
  projectId: string;
  host?: string;
  hidden?: boolean;
  onOpen: (sessionId: string) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setText((await api.vision(projectId)).text);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId]);

  useEffect(() => {
    setEditing(false);
    setDraft('');
  }, [projectId, host]);

  useEffect(() => {
    if (hidden) {
      setEditing(false);
      return;
    }
    void load();
  }, [hidden, load, host]);

  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === 'visible' && !hidden) void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [hidden, load]);

  const save = async (next: string): Promise<boolean> => {
    try {
      setText((await api.setVision(projectId, next)).text);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    }
  };

  const body = text.trim();

  const rewrite = async (): Promise<void> => {
    if (!body || rewriting || editing) return;
    setRewriting(true);
    try {
      const session = await api.createSession(projectId, 'Rewrite vision');
      await api.send(session.id, VISION_REWRITE_PROMPT, VISION_REWRITE_MODEL, null);
      onOpen(session.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRewriting(false);
    }
  };

  const startEdit = (): void => {
    setDraft(text);
    setEditing(true);
  };

  useEffect(() => {
    if (!editing) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const cancelEdit = (): void => {
    setEditing(false);
    setDraft('');
  };

  const saveEdit = async (): Promise<void> => {
    if (saving) return;
    if (draft === text) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      if (await save(draft)) {
        setEditing(false);
        setDraft('');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`vision-pane${editing ? ' vision-editing' : ''}`}
      id="pane-vision"
      role="tabpanel"
      aria-labelledby="tab-vision"
      hidden={hidden}
    >
      <div className="vision-body">
        {error && <div className="note note-error">{error}</div>}
        {editing ? (
          <textarea
            ref={editorRef}
            className="vision-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write the vision"
            aria-label="Vision markdown"
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck
          />
        ) : body ? (
          <div className="task-body">
            <Markdown text={text} />
          </div>
        ) : (
          <div className="empty">
            <p>No vision yet.</p>
            <p className="empty-hint">
              Write one below. It is saved as <code>vision.md</code> in the project root.
            </p>
          </div>
        )}
      </div>
      <div className="fabs">
        {editing ? (
          <button
            type="button"
            className="fab fab-quiet"
            onClick={cancelEdit}
            aria-label="Cancel editing"
          >
            <Icon name="close" size={22} strokeWidth={2.4} />
          </button>
        ) : (
          body && (
            <button
              type="button"
              className="fab"
              onClick={() => void rewrite()}
              disabled={rewriting}
              aria-label="Rewrite vision with Composer 2.5"
            >
              <Icon name="sparkles" size={22} strokeWidth={2.4} />
            </button>
          )
        )}
        {editing ? (
          <button
            type="button"
            className="fab"
            onClick={() => void saveEdit()}
            disabled={saving}
            aria-label="Save vision"
          >
            <Icon name="check" size={22} strokeWidth={2.4} />
          </button>
        ) : (
          <button type="button" className="fab" onClick={startEdit} aria-label="Edit vision">
            <Icon name="pencil" size={22} strokeWidth={2.4} />
          </button>
        )}
      </div>
      {!editing && (
        <Composer
          key={projectId}
          mode="store"
          allowImages={false}
          draftKey={`draft-vision:${projectId}`}
          running={false}
          queued={0}
          placeholder="Update the vision"
          submitLabel="Save vision"
          onSend={(next) => void save(next)}
          onStop={() => undefined}
        />
      )}
    </div>
  );
}

function hostPath(project: ProjectRow): string {
  if (project.activeHost && project.activeHost !== 'server' && project.desktop) {
    return project.desktop.path;
  }
  return project.exists ? project.path : `missing: ${project.path}`;
}

function hostHint(project: ProjectRow): string {
  if (!project.desktop) return '';
  if (!project.desktopOnline) return `${displayHost(project.desktop.host)} offline — home server`;
  if (project.preferredHost === 'server') {
    return `On home server — ${displayHost(project.desktop.host)} is connected`;
  }
  return `On ${displayHost(project.desktop.host)} (SSH)`;
}

export function HostSwitcher({
  project,
  disabled,
  onChange,
}: {
  project: ProjectRow;
  disabled?: boolean;
  onChange: (preferredHost: HostPref) => void;
}): JSX.Element | null {
  if (!project.desktop) return null;
  const prefer = project.preferredHost === 'server' ? 'server' : 'desktop';
  return (
    <>
      <ChoiceGroup
        label={`Host for ${project.name}`}
        value={prefer}
        disabled={disabled}
        options={[
          { id: 'desktop', label: displayHost(project.desktop.host) },
          { id: 'server', label: 'server' },
        ]}
        onChange={onChange}
      />
      <div className="settings-hint">{hostHint(project)}</div>
    </>
  );
}

/**
 * The phone's editor for `tools` in projects.json. Each card is one repo; the
 * toggles write that project's allowlist. Path and deploy stay read-only —
 * those are still a file you edit on the server. Host pin is SQLite, not the
 * file: the phone may flip PC vs home server without widening what a run can
 * touch.
 */
export function ProjectsConfigView({ hidden }: { hidden?: boolean }): JSX.Element {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setProjects(await api.projects());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (hidden) return;
    void load();
  }, [hidden, load]);

  useEffect(() => live.onDesktop(() => {
    if (!hidden) void load();
  }), [hidden, load]);

  const patchProject = async (
    project: ProjectRow,
    optimistic: Partial<ProjectRow>,
    send: () => Promise<ProjectRow>,
  ): Promise<void> => {
    if (savingId) return;
    setSavingId(project.id);
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, ...optimistic } : p)));
    try {
      const next = await send();
      setProjects((prev) => prev.map((p) => (p.id === next.id ? { ...p, ...next } : p)));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const setTools = (project: ProjectRow, tools: AgentToolId[]): Promise<void> =>
    patchProject(project, { tools }, () => api.setProjectTools(project.id, tools));

  const setHost = (project: ProjectRow, preferredHost: HostPref): Promise<void> => {
    if ((project.preferredHost ?? 'desktop') === preferredHost) return Promise.resolve();
    const activeHost =
      preferredHost === 'server' || !project.desktopOnline
        ? 'server'
        : (project.desktop?.host ?? 'server');
    return patchProject(project, { preferredHost, activeHost }, () =>
      api.setPreferredHost(project.id, preferredHost),
    );
  };

  return (
    <div
      className="git-pane"
      id="pane-config-projects"
      role="tabpanel"
      aria-labelledby="tab-config-projects"
      hidden={hidden}
    >
      <div className="git-body">
        {error && <div className="note note-error">{error}</div>}
        {projects.length === 0 && !error && (
          <div className="empty">
            <p>No projects configured.</p>
            <p className="empty-hint">
              Add repositories to <code>projects.json</code> on the server and pull to refresh.
            </p>
          </div>
        )}
        {projects.length > 0 && (
          <div className="project-config-list">
            {projects.map((project) => {
              const selected = toolsOf(project);
              return (
                <section key={project.id} className="git-section">
                  <div className="project-config-name">{project.name}</div>
                  <div className="settings-hint project-config-path">{hostPath(project)}</div>
                  {project.desktop && (
                    <>
                      <div className="git-section-title">Host</div>
                      <HostSwitcher
                        project={project}
                        disabled={savingId === project.id}
                        onChange={(host) => void setHost(project, host)}
                      />
                    </>
                  )}
                  <div className="git-section-title">Tools</div>
                  <div
                    className="btn-group"
                    role="group"
                    aria-label={`Agent tools for ${project.name}`}
                  >
                    {TOOL_OPTIONS.map((item) => {
                      const on = selected.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          aria-pressed={on}
                          className={`btn-group-item${on ? ' btn-group-item-on' : ''}`}
                          disabled={savingId === project.id}
                          onClick={() => void setTools(project, withTool(selected, item.id, !on))}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
            <div className="settings-hint">
              Commands from <code>tools/bin</code> a run in that repo is handed. Off
              means no briefing, no PATH entry, and the HTTP route that command posts
              to will refuse it.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Every model each provider will let you pick, and which of them this gateway
 * may use. The rows come from the provider's own CLI where there is a command
 * to ask (`cursor-agent --list-models`, `opencode models`); the backends that
 * have no such command show the catalog this build ships with and say so.
 *
 * What stays on is what chat and task Settings offer and what the homescreen
 * badges rotate through. With nothing on, nothing is filtered — every model
 * remains available, which is also the state a fresh install starts in.
 */
export function ModelsConfigView({ hidden, desktopHost }: { hidden?: boolean; desktopHost?: string }): JSX.Element {
  const { providers, enabled, loading, error } = useModelCatalog();
  const enabledIds = enabled.map((m) => m.id);

  const toggle = (model: ProviderModel, on: boolean): void => {
    const next = on
      ? [...enabled, model]
      : enabled.filter((m) => m.id !== model.id);
    void saveEnabledModels(next);
  };

  const toggleProvider = (provider: ProviderCatalog, on: boolean): void => {
    const others = enabled.filter((m) => m.agent !== provider.agent);
    void saveEnabledModels(on ? [...others, ...provider.models] : others);
  };

  return (
    <div
      className="git-pane"
      id="pane-config-models"
      role="tabpanel"
      aria-labelledby="tab-config-models"
      hidden={hidden}
    >
      <div className="git-body">
        {error && <div className="note note-error">{error}</div>}
        {desktopHost && <UpdatesConfigView hidden={hidden} host={desktopHost} embedded />}
        <div className="project-config-list">
          {providers.map((provider) => {
            const allOn =
              provider.models.length > 0 && provider.models.every((m) => enabledIds.includes(m.id));
            return (
              <section key={provider.agent} className="git-section">
                <div className="project-config-name">{provider.label}</div>
                <div className="settings-hint project-config-path">
                  {provider.source === 'cli'
                    ? `${provider.models.length} models from ${provider.command}`
                    : (provider.error ?? 'Built-in list')}
                </div>
                <div className="btn-group" role="group" aria-label={`${provider.label} models`}>
                  {provider.models.map((model) => {
                    const on = enabledIds.includes(model.id);
                    return (
                      <button
                        key={model.id}
                        type="button"
                        aria-pressed={on}
                        className={`btn-group-item${on ? ' btn-group-item-on' : ''}`}
                        onClick={() => toggle(model, !on)}
                      >
                        {model.label}
                      </button>
                    );
                  })}
                </div>
                {provider.models.length > 0 && (
                  <div className="git-reset-actions">
                    <button
                      type="button"
                      className="git-reset-btn"
                      onClick={() => toggleProvider(provider, !allOn)}
                    >
                      {allOn ? `Clear ${provider.label}` : `All ${provider.label}`}
                    </button>
                  </div>
                )}
              </section>
            );
          })}
          {providers.length === 0 && (
            <div className="empty">
              <p>{loading ? 'Asking each CLI…' : 'No providers.'}</p>
              <p className="empty-hint">
                Models are read from the agent CLIs installed on this gateway.
              </p>
            </div>
          )}
          <div className="settings-hint">
            {enabled.length === 0
              ? 'Nothing selected, so every model stays available in chats and tasks.'
              : `${enabled.length} models available in chats and tasks.`}
          </div>
        </div>
      </div>
      <div className="fabs">
        <button
          type="button"
          className="fab"
          onClick={() => void loadCatalog(true)}
          disabled={loading}
          aria-busy={loading}
          aria-label="Re-read models from each CLI"
        >
          <Icon name="refresh" size={22} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}

/** The tail of an update's log, pinned to the bottom while it is still writing. */
function UpdateLog({ snapshot }: { snapshot: CliUpdateSnapshot }): JSX.Element {
  const ref = useRef<HTMLPreElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [snapshot.output]);

  return (
    <pre ref={ref} className="command-log" aria-label="Update output">
      {snapshot.truncated ? '…\n' : ''}
      {snapshot.output || (snapshot.status === 'running' ? 'Starting…' : '')}
    </pre>
  );
}

/** What the row says under the buttons once a run has happened. */
function updateStatusText(snapshot: CliUpdateSnapshot): string {
  switch (snapshot.status) {
    case 'running':
      return 'Updating…';
    case 'success':
      return 'Updated.';
    case 'cancelled':
      return 'Stopped.';
    default:
      return `Failed: ${snapshot.error ?? 'unknown error'}`;
  }
}

/**
 * Every backend's CLI, its installed version, and a button that reinstalls it.
 * The command is fixed on the gateway for each host and backend; the phone
 * selects only the backend and configured host.
 *
 * The log is the install's stdout/stderr, same idea as a deploy. Nothing is
 * pushed here, so the pane polls while a run is in flight; the version under
 * the name is re-read once it finishes.
 */
export function UpdatesConfigView({
  hidden,
  host = 'server',
  embedded = false,
}: {
  hidden?: boolean;
  host?: string;
  embedded?: boolean;
}): JSX.Element {
  const [clis, setClis] = useState<CliUpdateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<AgentId | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh = false, quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    try {
      const list = await api.cliUpdates(refresh, host);
      setClis(list.clis);
      setError(null);
    } catch (err) {
      // A blip must not empty the pane under a running update.
      if (!quiet) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [host]);

  useEffect(() => {
    if (hidden) return;
    void load();
  }, [hidden, load]);

  const running = clis.some((cli) => cli.snapshot?.status === 'running');

  useEffect(() => {
    if (hidden || !running) return;
    const timer = setInterval(() => void load(false, true), 1200);
    return () => clearInterval(timer);
  }, [hidden, running, load]);

  const start = async (agent: AgentId): Promise<void> => {
    if (busy) return;
    setBusy(agent);
    setError(null);
    try {
      const snapshot = await api.startCliUpdate(agent, host);
      setClis((rows) => rows.map((row) => (row.agent === agent ? { ...row, snapshot } : row)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setBusy(null);
    }
  };

  const stop = async (agent: AgentId): Promise<void> => {
    try {
      await api.cancelCliUpdate(agent, host);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    await load(false, true);
  };

  return (
    <div
      className={embedded ? 'desktop-cli-updates' : 'git-pane'}
      id={embedded ? undefined : 'pane-config-updates'}
      role={embedded ? undefined : 'tabpanel'}
      aria-labelledby={embedded ? undefined : 'tab-config-updates'}
      hidden={hidden}
    >
      <div className={embedded ? undefined : 'git-body'}>
        {embedded && (
          <div className="git-section-head">
            <div className="project-config-name">Desktop CLI updates</div>
            <button
              type="button"
              className="git-reset-btn"
              onClick={() => void load(true)}
              disabled={loading}
              aria-busy={loading}
            >
              Refresh versions
            </button>
          </div>
        )}
        {error && <div className="note note-error">{error}</div>}
        <div className="project-config-list">
          {clis.map((cli) => {
            const active = cli.snapshot?.status === 'running';
            return (
              <section key={cli.agent} className="git-section">
                <div className="project-config-name">{cli.label}</div>
                <div className="settings-hint project-config-path">
                  {cli.path
                    ? `${cli.version ?? 'version unknown'} · ${cli.path}`
                    : `Not installed — "${cli.bin}" was not found on ${embedded ? 'Desktop' : 'this gateway'}.`}
                </div>
                <div className="git-reset-actions">
                  <button
                    type="button"
                    className="git-reset-btn"
                    disabled={busy !== null || active}
                    onClick={() => void start(cli.agent)}
                  >
                    {busy === cli.agent && <span className="spinner" />}
                    {cli.path ? 'Update' : 'Install'}
                  </button>
                  {active && (
                    <button
                      type="button"
                      className="git-reset-btn git-reset-btn-hard"
                      onClick={() => void stop(cli.agent)}
                    >
                      Stop
                    </button>
                  )}
                </div>
                <div className="settings-hint">
                  <code>{cli.command}</code>
                </div>
                {cli.snapshot && (
                  <>
                    <div className="settings-hint">{updateStatusText(cli.snapshot)}</div>
                    <UpdateLog snapshot={cli.snapshot} />
                  </>
                )}
              </section>
            );
          })}
          {clis.length === 0 && (
            <div className="empty">
              <p>{loading ? 'Reading CLI versions…' : 'No CLIs.'}</p>
              <p className="empty-hint">
                {embedded ? 'Reading CLIs on Desktop.' : 'Updates run on this gateway.'}
              </p>
            </div>
          )}
        </div>
      </div>
      {!embedded && <div className="fabs">
        <button
          type="button"
          className="fab"
          onClick={() => void load(true)}
          disabled={loading}
          aria-busy={loading}
          aria-label="Re-read the installed CLI versions"
        >
          <Icon name="refresh" size={22} strokeWidth={2.4} />
        </button>
      </div>}
    </div>
  );
}

/**
 * Buttons that start allowlisted processes on the connected desktop. Visible
 * only while that PC answers SSH — the tab is hidden when it is not.
 * The log is the SSH session's stdout/stderr, same idea as a deploy.
 */
export function CommandsView({
  project,
  hidden,
}: {
  project: ProjectRow;
  hidden?: boolean;
}): JSX.Element {
  const commands = project.commands ?? [];
  const first = commands[0];
  const commandId = first?.id;
  const defaultPort = first?.defaultPort ?? 3000;
  const [portText, setPortText] = useState(() =>
    first ? readStoredPort(project.id, first.id, defaultPort) : String(defaultPort),
  );
  const [busy, setBusy] = useState<'start' | 'stop' | null>(null);
  const [snap, setSnap] = useState<CommandSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    setError(null);
    setSnap(null);
    if (commandId) {
      setPortText(readStoredPort(project.id, commandId, defaultPort));
    }
  }, [project.id, commandId, defaultPort]);

  const parsedPort = parsePort(portText);

  const load = useCallback(async (): Promise<void> => {
    if (!commandId) return;
    try {
      const state = await api.commandState(project.id, commandId);
      setSnap(state.snapshot);
    } catch {
      // Keep the last snapshot: a blip must not hide an in-flight command.
    }
  }, [commandId, project.id]);

  useEffect(() => {
    if (hidden || !commandId) return;
    void load();
  }, [hidden, commandId, load]);

  useEffect(() => {
    if (!commandId) return;
    return live.onCommand((next) => {
      if (next.projectId === project.id && next.commandId === commandId) setSnap(next);
    });
  }, [commandId, project.id]);

  useEffect(() => {
    let prev = live.status;
    return live.onStatus((status) => {
      if (prev !== 'live' && status === 'live' && !hidden) void load();
      prev = status;
    });
  }, [hidden, load]);

  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [snap?.output]);

  const running = snap?.status === 'running';

  const run = async (action: 'start' | 'stop'): Promise<void> => {
    const cmd = commands[0];
    if (!cmd || busy || parsedPort === null) return;
    setBusy(action);
    setError(null);
    storePort(project.id, cmd.id, String(parsedPort));
    try {
      const next =
        action === 'start'
          ? await api.runCommand(project.id, cmd.id, parsedPort)
          : await api.stopCommand(project.id, cmd.id, parsedPort);
      setSnap(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="git-pane"
      id="pane-config-commands"
      role="tabpanel"
      aria-labelledby="tab-config-commands"
      hidden={hidden}
    >
      <div className="git-body">
        {error && <div className="note note-error">{error}</div>}
        {commands.length === 0 && !error && (
          <div className="empty">
            <p>No commands for this project.</p>
            <p className="empty-hint">Commands start on the connected desktop.</p>
          </div>
        )}
        {first && (
          <section className="git-section">
            <div className="field-row">
              <label htmlFor="command-port">Port</label>
              <input
                id="command-port"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={portText}
                onChange={(e) => {
                  setPortText(e.target.value);
                  storePort(project.id, first.id, e.target.value);
                }}
                aria-label="Dev server port"
              />
            </div>
            {parsedPort === null && portText.trim() !== '' && (
              <div className="settings-hint">Port must be an integer from 1 to 65535.</div>
            )}
            <div className="git-reset-actions">
              <button
                type="button"
                className="git-reset-btn"
                disabled={busy !== null || parsedPort === null || running}
                onClick={() => void run('start')}
              >
                {busy === 'start' && <span className="spinner" />}
                {first.label}
              </button>
              <button
                type="button"
                className="git-reset-btn git-reset-btn-hard"
                disabled={busy !== null || parsedPort === null}
                onClick={() => void run('stop')}
              >
                {busy === 'stop' && <span className="spinner" />}
                Stop
              </button>
            </div>
            <div className="settings-hint">
              {running
                ? `Running on ${displayHost(project.desktop?.host ?? 'desktop')}:${snap?.port ?? parsedPort ?? defaultPort}.`
                : project.desktop
                  ? `Runs on ${displayHost(project.desktop.host)} in ${project.desktop.path}.`
                  : 'Runs on the connected desktop.'}
            </div>
            <pre ref={logRef} className="command-log" aria-label="Command output">
              {snap?.truncated ? '…\n' : ''}
              {snap?.output || (running ? 'Starting…' : '')}
            </pre>
          </section>
        )}
      </div>
    </div>
  );
}
